import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Button,
  InputBase,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import MapIcon from '@material-ui/icons/Map';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import OutlinedFlagIcon from '@material-ui/icons/OutlinedFlag';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { get, deleteD, patch } from 'api/requests';
import { urlFrequentRoutes } from 'api';
import { I18nContext } from 'translations';
import { modalMessage } from 'utils';

export default function ModalFrequentRoutesList({ open, setOpen, onSelect }) {
  const { translate } = useContext(I18nContext);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const inputRef = useRef(null);

  const fetchRoutes = () => {
    setLoading(true);
    get(urlFrequentRoutes)
      .then(({ data }) => setRoutes(Array.isArray(data) ? data : (data?.frequent_routes || data?.data || [])))
      .catch(() => setRoutes([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) fetchRoutes();
    else setSearch('');
  }, [open]);

  useEffect(() => {
    if (editingId && inputRef.current) inputRef.current.focus();
  }, [editingId]);

  const startEdit = (route) => {
    setEditingId(route._id);
    setEditingName(route.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveEdit = async (id) => {
    if (!editingName.trim()) return;
    setSavingId(id);
    try {
      await patch(`${urlFrequentRoutes}/${id}`, { frequent_route: { name: editingName.trim() } });
      setRoutes((prev) => prev.map((r) => r._id === id ? { ...r, name: editingName.trim() } : r));
      setEditingId(null);
    } catch (_) {}
    setSavingId(null);
  };

  const handleDelete = async (id, name) => {
    const result = await modalMessage(
      translate('app.commonRoutes.deleteConfirmTitle'),
      `"${name}"`,
      'warning',
      translate('delete'),
      true,
      true,
      translate('cancel'),
      true
    );
    if (!result.isConfirmed) return;
    setDeletingId(id);
    try {
      await deleteD(`${urlFrequentRoutes}/${id}`);
      setRoutes((prev) => prev.filter((r) => r._id !== id));
    } catch (_) {}
    setDeletingId(null);
  };

  const handleSelect = (route) => {
    onSelect && onSelect(route);
    setOpen(false);
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}>

      <Box
        style={{
          background: '#EEE8F8',
          padding: '18px 20px 12px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          position: 'relative',
        }}
      >
        <Box
          style={{
            background: '#6f2f82',
            borderRadius: '50%',
            width: 54,
            height: 54,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MapIcon style={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography style={{ fontWeight: 700, fontSize: 18, color: '#1a1a1a', lineHeight: 1.3 }}>
            {translate('app.commonRoutes.listTitle')}
          </Typography>
          <Typography variant="caption" style={{ color: '#666' }}>
            {routes.length} {translate('app.commonRoutes.saved')}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          style={{
            marginLeft: 'auto',
            marginRight: 15,
            background: '#fff',
            borderRadius: 20,
            padding: '4px 10px',
            gap: 4,
            minWidth: 140,
          }}
        >
          <SearchIcon style={{ fontSize: 16, color: '#999' }} />
          <InputBase
            placeholder={translate('search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ fontSize: 13, flex: 1 }}
          />
        </Box>
        <IconButton
          size="small"
          onClick={() => setOpen(false)}
          style={{ position: 'absolute', top: 6, right: 6, color: '#555' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent style={{ padding: '0 24px 20px', background: '#fff' }}>
        <Box
          display="flex"
          alignItems="flex-start"
          style={{
            background: '#FFF8E1',
            border: '1px solid #F6D77B',
            borderRadius: 10,
            padding: '10px 12px',
            gap: 8,
            marginTop: 14,
            marginBottom: 10,
          }}
        >
          <InfoOutlinedIcon style={{ color: '#B07A00', fontSize: 18, marginTop: 1, flexShrink: 0 }} />
          <Typography variant="caption" style={{ color: '#5C4300', lineHeight: 1.4 }}>
            {translate('app.commonRoutes.cityNotice')}
          </Typography>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ height: 160 }}>
            <CircularProgress />
          </Box>
        ) : routes.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ height: 140 }}>
            <Typography color="textSecondary">{translate('app.commonRoutes.noRoutes')}</Typography>
          </Box>
        ) : (
          routes.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())).map((route, i) => (
            <Box
              key={route._id}
              style={{
                paddingTop: 10,
                paddingBottom: 10,
                borderTop: i > 0 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              {/* Route name row */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                {editingId === route._id ? (
                  <Box display="flex" alignItems="center" style={{ flex: 1, gap: 4 }}>
                    <InputBase
                      inputRef={inputRef}
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(route._id);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      style={{
                        flex: 1,
                        fontWeight: 700,
                        fontSize: 15,
                        color: '#1a1a1a',
                        borderBottom: '2px solid #6f2f82',
                        paddingBottom: 1,
                      }}
                    />
                    <IconButton size="small" onClick={() => saveEdit(route._id)} style={{ color: '#6f2f82', padding: 4 }}>
                      {savingId === route._id ? <CircularProgress size={14} color="inherit" /> : <CheckIcon style={{ fontSize: 16 }} />}
                    </IconButton>
                    <IconButton size="small" onClick={cancelEdit} style={{ color: '#aaa', padding: 4 }}>
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>
                      {route.name}
                    </Typography>
                    <IconButton size="small" onClick={() => startEdit(route)} style={{ color: '#aaa', padding: 2 }}>
                      <EditOutlinedIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                )}
                {editingId !== route._id && (
                  <IconButton
                    size="small"
                    disabled={deletingId === route._id}
                    onClick={() => handleDelete(route._id, route.name)}
                    style={{ color: '#aaa' }}
                  >
                    {deletingId === route._id
                      ? <CircularProgress size={16} color="inherit" />
                      : <DeleteOutlineIcon style={{ fontSize: 20 }} />
                    }
                  </IconButton>
                )}
              </Box>

              {/* Origin */}
              <Box display="flex" alignItems="flex-start" style={{ marginTop: 10, gap: 10 }}>
                <RoomOutlinedIcon style={{ fontSize: 15, color: '#6f2f82', marginTop: 2, flexShrink: 0 }} />
                <Typography style={{ fontSize: 13, color: '#555' }}>{route.address}</Typography>
              </Box>

              {/* Stops */}
              {route.stops?.map((stop, si) => (
                <Box key={si} display="flex" alignItems="flex-start" style={{ marginTop: 5, gap: 10 }}>
                  <OutlinedFlagIcon style={{ fontSize: 15, color: '#6f2f82', marginTop: 2, flexShrink: 0 }} />
                  <Typography style={{ fontSize: 13, color: '#555' }}>{stop.address}</Typography>
                </Box>
              ))}

              {/* Button */}
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{
                  marginTop: 14,
                  borderRadius: 24,
                  paddingLeft: 14,
                  paddingRight: 14,
                  paddingTop: 4,
                  paddingBottom: 4,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  boxShadow: 'none',
                }}
                onClick={() => handleSelect(route)}
              >
                {translate('app.commonRoutes.useRoute')} →
              </Button>
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}
